import { GridApi } from 'ag-grid-community';
import { flatten, uniq } from 'lodash';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TaskId, TaskIdV2 } from 'api/models';
import { TaskAPI } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { RootState } from 'data';
import { deleteTask } from 'data/tasks/slice';
import { useTranslate } from 'hooks/use-translation';
import { logger } from 'services';
import { CreatableEditableRefreshableContent } from 'stores/core';
import { deleted, deleting, restored, Task } from 'stores/tasks';
import { queueMacrotask } from 'utils/macrotask';

type DeleteTasks = (ids: TaskIdV2[]) => void;

const getAllSubTaskList = (
  id: number,
  taskById: Record<number, CreatableEditableRefreshableContent<Task>>,
  taskIds: number[] = [],
) => {
  const task = taskById[id][1];

  if (!task) {
    return taskIds;
  }
  const { taskId, subTaskOrder = [] } = task;

  taskIds.push(taskId);
  subTaskOrder.forEach((subTaskId) => {
    const subSubTaskId = taskById[subTaskId][1]?.taskId;

    if (!subSubTaskId) {
      return;
    }
    getAllSubTaskList(subSubTaskId, taskById, taskIds);
  });

  return taskIds;
};

export const useDeleteWithPagination = (gridApi?: GridApi) => {
  return useCallback(
    (ids: TaskIdV2[]) => {
      const constructRequest = ([, id]: TaskIdV2): TaskId => {
        const row = gridApi?.getRowNode(String(id))?.data;

        return TaskId(row?.project, id);
      };
      const constructDeleteApi = ([_, projectId, taskId]: TaskId) =>
        TaskAPI.deleteTask(projectId, taskId).then(() => {
          const rowNode = gridApi?.getRowNode(`${taskId}`);

          if (!rowNode) {
            return;
          }
          gridApi?.applyServerSideTransaction({
            route: [],
            remove: [rowNode.data],
          });
        });
      const requests: TaskId[] = ids.map(constructRequest);

      return Promise.all(requests.map(constructDeleteApi));
    },
    [gridApi],
  );
};

export const useDeleteWithoutPagination = (): DeleteTasks => {
  const dispatch = useDispatch();
  const translate = useTranslate();
  const { byId } = useSelector(({ tasks_v2 }: RootState) => tasks_v2);

  return useCallback(
    (ids: TaskIdV2[]) => {
      const givenIds = ids.map(([, id]) => id);
      const subTaskIds = uniq(
        flatten(givenIds.map((id) => getAllSubTaskList(id, byId))),
      );
      let taskPartofRevenueRecognitionPlan = false;

      queueMacrotask(async () => {
        const tasks = subTaskIds
          .map((id) => byId[id])
          .map(([, x]) => x as Task)
          .filter(Boolean);

        dispatch(deleting(tasks));
        await Promise.all(
          tasks.map(async (task) => {
            try {
              // server would deleted sub task given the parent task. So api for deleting the user selected task is enough.
              if (givenIds.includes(task.taskId)) {
                await TaskAPI.deleteTask(task.project, task.taskId);
              }
              dispatch(deleted([task]));
              dispatch(deleteTask(TaskId(task.project, task.taskId)));
            } catch (error) {
              taskPartofRevenueRecognitionPlan = true;
              dispatch(restored([task]));
              logger.error('delete task failed', { error });
            }
          }),
        );

        if (taskPartofRevenueRecognitionPlan) {
          toastService({
            title: translate('revenue_recognition.task.list.delete.fail.title'),
            message: translate(
              'revenue_recognition.task.list.delete.fail.message',
            ),
          }).success();
        }
      });
    },
    [byId, dispatch, translate],
  );
};

export const useDeleteTasks = (isPaginated: boolean, gridApi?: GridApi) => {
  const deleteWithPagination = useDeleteWithPagination(gridApi);
  const deleteWithoutPagination = useDeleteWithoutPagination();

  return useCallback(
    (ids: TaskIdV2[]) => {
      if (isPaginated) {
        return deleteWithPagination(ids);
      }

      return deleteWithoutPagination(ids);
    },
    [deleteWithPagination, deleteWithoutPagination, isPaginated],
  );
};
